import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MailCheck, Send, Copy, Check } from "lucide-react";

export default function AdminReminders() {
  const [tab, setTab] = useState("due");
  const [orders, setOrders] = useState([]);
  const [delayDays, setDelayDays] = useState(3);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reminders?status=${tab}`);
      setOrders(data.orders);
      setDelayDays(data.reminder_delay_days);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const send = async (id) => {
    try {
      const { data } = await api.post(`/admin/reminders/${id}/send`);
      toast.success(`Reward code generated: ${data.reward_code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reminder.");
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div data-testid="admin-reminders">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Post-Purchase</p>
          <h1 className="font-serif text-4xl">Review Reminders</h1>
        </div>
      </div>
      <p className="text-sm text-[#4A4A4A] max-w-2xl mb-8">
        Orders marked <span className="font-mono-alt">delivered</span> {delayDays}+ days ago
        become eligible for a gentle review nudge. Sending generates a unique{" "}
        <span className="font-mono-alt">PHOTO25-XXXX</span> code (25% off, min ₹5,000) that the
        customer can use if they post a photo review. When Resend is enabled, this will also
        dispatch the email automatically — for now, you can copy the code and share it.
      </p>

      <div className="flex gap-2 mb-6 border-b border-black/10">
        {[
          { k: "due", label: "Due" },
          { k: "sent", label: "Sent" },
          { k: "all", label: "All delivered" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-[11px] uppercase tracking-nav border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-[#5C1A1B] text-[#5C1A1B]" : "border-transparent text-[#7A7A7A] hover:text-[#1A1A1A]"
            }`}
            data-testid={`admin-reminders-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-[#7A7A7A]">Loading…</p>}
      {!loading && orders.length === 0 && (
        <div className="bg-white border border-black/5 p-10 text-center" data-testid="admin-reminders-empty">
          <MailCheck size={30} strokeWidth={1.3} className="mx-auto text-[#5C1A1B] mb-3" />
          <p className="font-serif text-2xl">Nothing to send.</p>
          <p className="text-sm text-[#7A7A7A] mt-1">
            {tab === "due" ? "No delivered orders are due for a reminder yet." : "No reminders in this bucket."}
          </p>
        </div>
      )}
      {!loading && orders.length > 0 && (
        <div className="bg-white border border-black/5">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-nav text-[#7A7A7A] border-b border-black/5">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Delivered</th>
                <th className="px-4 py-3">Reminder</th>
                <th className="px-4 py-3">Reward Code</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} className="border-b border-black/5" data-testid={`admin-reminder-row-${o.order_id}`}>
                  <td className="px-4 py-3 font-mono-alt text-xs">{o.order_id}</td>
                  <td className="px-4 py-3">
                    {o.shipping_address?.full_name}
                    <br />
                    <span className="text-[11px] text-[#7A7A7A]">{o.email}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">{o.delivered_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] uppercase tracking-nav ${
                      o.review_reminder_status === "sent" ? "text-[#388E3C]" :
                      o.review_reminder_status === "due" ? "text-[#5C1A1B]" : "text-[#7A7A7A]"
                    }`}>
                      {o.review_reminder_status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {o.review_reward_code ? (
                      <button
                        onClick={() => copy(o.review_reward_code)}
                        className="font-mono-alt text-xs bg-[#F3EFEA] px-2 py-1 flex items-center gap-1 hover:bg-[#5C1A1B] hover:text-white transition-colors"
                        data-testid={`admin-reminder-copy-${o.order_id}`}
                      >
                        {o.review_reward_code}
                        {copied === o.review_reward_code ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    ) : (
                      <span className="text-[11px] text-[#7A7A7A]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {o.review_reminder_status !== "sent" ? (
                      <button
                        onClick={() => send(o.order_id)}
                        className="bg-[#5C1A1B] text-white text-[11px] uppercase tracking-nav px-3 py-1.5 hover:bg-[#701C1D] transition-colors flex items-center gap-1 ml-auto"
                        data-testid={`admin-reminder-send-${o.order_id}`}
                      >
                        <Send size={12} /> Send
                      </button>
                    ) : (
                      <span className="text-[11px] text-[#388E3C] uppercase tracking-nav">Sent</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
